// The shapes this module's API returns and accepts.

import type { Capability } from "./authority.js";
import type { AllocationSlice } from "@/finance/reporting/model/portfolio.js";
import type { BalanceSeries } from "@/finance/balances/model/balance-series.js";
import type { NetWorthSlice } from "@/finance/balances/model/net-worth.js";
import type { MonthSample } from "@/finance/balances/model/sampling.js";
import type { FlowItem } from "@/finance/reporting/model/flows.js";
import type { Opening } from "@/finance/reporting/model/openings.js";
import type { HoldingGroup } from "@/finance/registry/model/holding-group.js";
import type { FxAttributionOmission } from "@/finance/reporting/model/fx-attribution.js";
import { BASE_CURRENCY } from "@/finance/model/constants.js";
import { IN_PARTS, OUT_PARTS } from "@/finance/reporting/model/parts.js";
import { dateStr } from "@/platform/http";
import { z } from "zod";
import {
  ACCOUNT_TYPES,
  ASSET_TYPES,
  INSTITUTION_TYPES,
  type DATA_QUALITY_ACTIONS,
  type DATA_QUALITY_CONDITIONS,
} from "@/finance/model/vocabulary.js";

export { ACCOUNT_TYPES, ASSET_TYPES, INSTITUTION_TYPES };

// A non-base symbol still held but that couldn't be priced/costed (e.g. no live
// quote) — shown so a gap in the list is visible/debuggable, not silent.
type FxAttributionOmissionDto = FxAttributionOmission;

// ---- markets ----
export interface MarketQuote {
  symbol: string;
  price: number | null;
  changePct: number | null;
  currency: string | null;
}

export interface WatchlistEntry {
  id: number;
  symbol: string;
  label: string;
  position: number;
  quote: MarketQuote | null;
}

// A market-news headline surfaced for the dashboard. `symbol`/`changePct` are set
// when the item was bubbled up because one of your holdings/watchlist names made a
// notable move; `important` marks those for emphasis.
export interface NewsItem {
  title: string;
  summary: string | null; // a short blurb, where the source gives one
  source: string | null;
  url: string;
  publishedAt: string; // ISO
  symbol: string | null;
  changePct: number | null;
  important: boolean;
}

export interface MarketNewsResponse {
  asOf: string;
  items: NewsItem[];
}

// ---- transactions ledger ----
/** An account, as a picker sees it. */
export interface Account {
  id: number;
  name: string;
  /** current | savings | deposit | credit_card | prepaid_card | cash
   *  | brokerage | pension — `ACCOUNT_TYPES`. */
  type: string;
  institution: string;
  /** What the institution calls it: the account's identity there. */
  reference: string;
  /** The currency of the account's one balance; null on a brokerage, which
   *  defines its balances each in its own currency. */
  currency: string | null;
  /** The day it stopped being an account, or `null` while it is open. */
  closedDate: string | null;
  /** What an extension manages on this account, by capability, with its name.
   *  Anything absent is the person's to change. */
  managed: Partial<Record<Capability, string>>;
}

/** One account, with everything the account modal needs to open on it. */
export interface AccountDetail extends Account {
  /** Every balance this account defines, stated against or not. */
  balances: Balance[];
  /** The groups its balances chart in. */
  groups: BalanceGroup[];
}

/** One balance an account defines: a label and a currency. */
export interface Balance {
  id: number;
  accountId: number;
  name: string;
  currency: string;
  groupId: number | null;
  /** `holdings` on a balance the application maintains from what the
   *  account holds; null on a balance a document or a person states. */
  derivedBy: string | null;
  /** The newest day anything was stated for it, projections included. */
  lastStatedDate: string | null;
  observations: number;
}

/** A stacked chart's worth of an account's balances, named by the person. */
export interface BalanceGroup {
  id: number;
  accountId: number;
  name: string;
  position: number;
}

/**
 * What deleting an account would take with it, counted per fact.
 *
 * Shown before the deletion, never guessed at: an account with four years of
 * transactions and a hundred trades behind it is a different decision from an
 * empty one, and the person makes it.
 */
export interface AccountFootprint {
  accountId: number;
  balances: number;
  transactions: number;
  trades: number;
  dividends: number;
  interest: number;
  holdings: number;
  lots: number;
}

/** One figure stated against a balance, as the account modal lists it. */
export interface Observation {
  id: number;
  balanceId: number;
  accountId: number;
  /** The balance's name. */
  balance: string;
  currency: string;
  /** The day this is the closing figure for. */
  asOf: string;
  /** `null` is an explicit clear: "no value here", not a missing row. */
  amount: number | null;
  /** Which of a movement's dates the figure counts by, as stated: `booked`,
   *  the ledger's own, or `traded` / `value`, which the view reads on it. */
  basis: string;
  /** imported | manual | fetched, or null for a row written before the app
   *  recorded a channel. */
  origin: string | null;
  /** Who stated it, as a person reads it: the extension behind the run that
   *  wrote it, or null for the person's own figure. */
  statedBy: string | null;
}

/** Interest an account paid or charged, as a row. */
export interface InterestRow {
  id: number;
  accountId: number;
  account: string;
  accountInstitution: string | null;
  paidDate: string;
  amount: number;
  currency: string;
  note: string | null;
  /** The movement this payment is on the account's ledger. */
  transactionId: number;
  origin: string;
}

/** One position a document states an account held, on the day it states it. */
export interface HoldingRow {
  id: number;
  accountId: number;
  symbol: string;
  asOf: string;
  quantity: number;
  /** What the document puts the position at, in `statedCurrency`. */
  statedValue: number | null;
  /** What the document says it cost, same currency. */
  statedCost: number | null;
  statedCurrency: string | null;
  origin: string | null;
}

/** One asset an account holds, as the lots say it does and as a statement
 *  last said it did, side by side. */
export interface PositionRow {
  accountId: number;
  symbol: string;
  /** The position of record today. */
  quantity: number;
  /** Whether a statement anchors the quantity, or the trades alone answer it. */
  basis: "statement" | "trades";
  /** The anchoring statement's day, where there is one. */
  statedDate: string | null;
  /** The anchoring statement row, for removing it. */
  statedId: number | null;
  /** The position at the latest stored price, else at the value the statement
   *  put on it while nothing has moved it since. */
  value: number | null;
  valueCurrency: string | null;
}

/** One acquisition still on the books, read-only: lots are the FIFO rule's to
 *  write, or a document's to name, never a form's. */
export interface LotRow {
  /** The engine's own key for it — `lot:<id>` for one a document named,
   *  `trade:<id>` for one the trades imply. A derived lot has no row and so
   *  no id of its own; this is what a reader keys on. */
  key: string;
  accountId: number;
  symbol: string;
  acquiredAt: string;
  quantity: number;
  /** The trade that opened it, which is how a sell names the lot it closes. */
  openingTradeId: number | null;
  /** What is left after the disposals below. */
  remaining: number;
  /** The day it becomes sellable, for a restricted grant. */
  sellableFrom: string | null;
  /** Whether a document named this lot outright, with a lock-up date nothing
   *  else knows; the rest are what the trades imply. */
  documented: boolean;
  /** Opened by a purchase an extension's rule expects, which no document has
   *  confirmed yet. */
  projected: boolean;
  /** How many sales have eaten into it. */
  disposals: number;
}

// One ledger row, joined with its account name. `needsReview` is true when the
// row has no primary category (an untagged import row or a blank correction).
export interface Transaction {
  id: number;
  accountId: number | null;
  account: string;
  /** Null for an account with no institution on file. Present so two accounts
   *  that happen to share a name (a "3a portfolio" at each of two pension
   *  institutions) never read as the same account. */
  accountInstitution: string | null;
  /** The day it counts on: the day the institution booked it. */
  bookedDate: string; // YYYY-MM-DD
  /** The day it was initiated — a purchase, a trade — where printed. */
  tradeDate: string | null;
  /** The value date, where printed: interest and settlement. */
  valueDate: string | null;
  /** The identifier the source printed for the movement. */
  externalRef: string | null;
  amount: number; // signed: debit negative, credit positive
  currency: string;
  originalAmount: number | null;
  originalCurrency: string | null;
  /** The source's whole wording for the movement; a table may shorten it. */
  description: string;
  primary: string | null;
  secondary: string | null;
  confidence: number | null; // classifier confidence on a raw import (else null)
  source: string; // imported | manual
  /** The import that wrote or last restated it: the extension behind it (null
   *  for one of the application's own), the file and the day. Null for a row
   *  entered by hand or carried with no import on record. */
  imported: { by: string | null; file: string; on: string } | null;
  note: string | null; // free-text annotation
  needsReview: boolean;
}

export interface TransactionListResponse {
  rows: Transaction[];
  total: number; // matching the current filter (for pagination)
  /** Untagged rows within the same filter the table is showing, which is what
   *  makes the count mean something where it stands. */
  needsReview: number;
}

// A classifier suggestion for one needs-review row (nothing is written until the
// user applies it). Only rows the model could tag are returned.
export interface TxnSuggestionRow {
  id: number;
  bookedDate: string;
  account: string;
  description: string;
  amount: number;
  currency: string;
  primary: string; // suggested category
  secondary: string | null;
  confidence: number; // 0..1
}

export interface SuggestReviewResponse {
  rows: TxnSuggestionRow[];
  /** Needs-review rows the model could NOT tag (left for manual handling). */
  unmatched: number;
}

// ---- category taxonomy ----
/**
 * One row of the expense taxonomy, primary or secondary.
 *
 * `systemKey` is set only on the categories the application itself depends on
 * (`system` is just "is that set"). Those can be **renamed but not deleted** —
 * the engine branches on the key, so the name is free. Everything else is
 * yours. A category a person creates never gets a key: claiming a row for the
 * application is a code change, not user input.
 */
export interface Category {
  id: number;
  name: string;
  type: "primary" | "secondary";
  systemKey: string | null;
  system: boolean;
  position: number;
}

export interface CategoriesResponse {
  primaries: Category[];
  secondaries: Category[];
}

// ---- investments: trades / dividends / portfolio / performance ----
// One buy/sell row, joined with its broker account name. Values are kept in the
// native trade currency, plus a CHF cache converted at the trade's own day FX.
export interface Trade {
  id: number;
  executedAt: string; // YYYY-MM-DD or full ISO when a time is known
  symbol: string;
  accountId: number;
  broker: string; // the account's own name
  type: string; // buy | sell
  quantity: number | null;
  price: number | null;
  amount: number; // native cost (buy) / proceeds (sell)
  fee: number | null;
  feeCurrency: string | null;
  feeChf: number | null;
  currency: string;
  amountChf: number | null;
  realizedPlChf: number | null;
  /** How the trade arrived: `imported` or `manual`. */
  source: string;
  /** A synthetic buy standing in for a holding that predates the record, rather
   *  than a real execution. The money was never spent inside the reporting
   *  period, so it is not investment expenditure — and the list badges it so it
   *  is never read as a purchase that happened. */
  openingPosition: boolean;
  /** The lots a sell closes, where the person chose them; null is FIFO. */
  lotChoice: LotChoice[] | null;
}

/** One lot a sell takes from, named by the trade that opened it. */
export interface LotChoice {
  trade: number;
  quantity: number;
}

const lotChoiceSchema = z
  .array(
    z
      .object({
        trade: z.number().int().positive(),
        quantity: z.coerce.number().finite().positive(),
      })
      .strict(),
  )
  .min(1);

export interface Dividend {
  id: number;
  paidDate: string;
  symbol: string;
  accountId: number;
  broker: string;
  amount: number; // gross dividend, native
  withholdingTax: number | null;
  currency: string;
  amountChf: number | null;
  withholdingTaxChf: number | null;
  source: string;
}

export interface TradeListResponse {
  rows: Trade[];
  total: number;
}

export interface DividendListResponse {
  rows: Dividend[];
  total: number;
}

export interface PortfolioHolding {
  symbol: string;
  account: string;
  quantity: number;
  group: HoldingGroup;
  currency: string | null; // this holding's own trading currency (USD for crypto) — basis for every *Native field below
  valueChf: number | null; // current market value (CHF) — for cross-currency sort/totals only, not displayed per-row
  valueUsd: number | null; // current market value (USD) — for the allocation and Top Holdings widgets
  netInvestedChf: number;
  costBasisChf: number; // average-cost basis of the shares still held (CHF)
  priceChf: number | null; // current price per unit (CHF)
  priceNative: number | null; // current price per unit, in `currency` — what the Positions table shows
  valueNative: number | null; // current market value, in `currency`
  netInvestedNative: number; // native-currency mirror of netInvestedChf, no FX folded in
  costBasisNative: number; // native-currency cost basis of the shares still held, no FX folded in
  changePct: number | null; // day change % vs the prior session (null if no live quote)
  dailyPlChf: number | null; // today's mark-to-market change (CHF)
  dailyPlNative: number | null; // today's mark-to-market change, in `currency`
  unrealizedPlChf: number | null; // valueChf − costBasisChf
  unrealizedPlNative: number | null; // valueNative − costBasisNative, in `currency` — currency-clean P&L, no FX drift
}

/**
 * One period of holding one asset in one account: opened by the first
 * acquisition after the position was flat, closed when it returns to flat.
 *
 * Selling out and buying back is two positions, not one with a blended lifetime
 * number — and it is also the only shape in which a CLOSED position is visible,
 * since the portfolio values what is still held.
 *
 * A CHF figure is `null` where something in it could not be valued (an asset
 * with no price history), never a partial sum presented as a total.
 */
export interface PositionEpisode {
  account: string;
  symbol: string;
  /** 1-based, per (account, symbol), oldest first. */
  episode: number;
  openedAt: string;
  /** `null` while any of it is still held. */
  closedAt: string | null;
  quantityAcquired: number;
  quantityRemaining: number;
  /** How many disposals closed part of it. */
  sales: number;
  /** What the acquisitions cost, fees included. */
  investedChf: number | null;
  realizedPlChf: number | null;
  /** Only on an open episode: what remains is worth, and its gain so far. */
  valueChf: number | null;
  unrealizedPlChf: number | null;
}

export interface PortfolioResponse {
  asOf: string;
  totalValueChf: number;
  totalValueUsd: number | null;
  totalCostBasisChf: number;
  totalCostBasisUsd: number | null;
  totalUnrealizedPlChf: number;
  totalUnrealizedPlUsd: number | null;
  totalDailyPlChf: number;
  totalDailyPlUsd: number | null;
  // portfolio value / unrealized P&L day-move %, over prior-day value — a
  // same-day CHF→USD re-denomination doesn't change these ratios (dividing
  // both a total and its own delta by the same day's rate leaves the % the
  // same), so there's a single % pair shared by the CHF and USD readings.
  dailyChangePct: number | null;
  unrealizedDailyChangePct: number | null;
  holdings: PortfolioHolding[];
  allocation: AllocationSlice[];
}

// One timescale of the performance widget: the benchmark price return alongside
// the portfolio's time-weighted return over the same window (period returns as
// %, not annualised — 1D is the day's move, 6M the six-month move).
export interface PerformanceWindow {
  label: string; // "1D" | "1W" | "1M" | "3M" | "6M" | "1Y"
  benchmarkPct: number | null;
  twrPct: number | null;
}

// A day of the continuous whole-portfolio TWR index vs. a benchmark, both
// rebased to 100: the line chart below Market's performance bars, from the same
// chain-linked numbers as the bars.
export interface PerformancePoint {
  date: string; // YYYY-MM-DD
  portfolioIndex: number;
  benchmarkIndex: number | null;
}

export interface PortfolioPerformance {
  benchmarkLabel: string; // e.g. "S&P 500"
  asOf: string;
  windows: PerformanceWindow[];
  series: PerformancePoint[]; // ascending by date; empty when there's no data
}

// ---- FX attribution (Financial Analysis) ----
// Each non-base-currency holding's return decomposed into what the asset did in
// its own currency (local) vs. what the currency did against CHF (ccy):
// (1 + totalReturnPct/100) = (1 + localReturnPct/100)·(1 + ccyReturnPct/100).
export interface FxAttributionHoldingDto {
  symbol: string;
  name: string | null;
  currency: string;
  kind: "security" | "cash";
  quantity: number;
  chfValue: number;
  weightPct: number; // share of the non-base book value
  localReturnPct: number;
  ccyReturnPct: number;
  totalReturnPct: number;
}

export interface FxAttributionResponse {
  asOf: string;
  baseCurrency: string;
  holdings: FxAttributionHoldingDto[]; // non-base holdings, largest CHF value first
  omitted: FxAttributionOmissionDto[];
  book: {
    chfValue: number;
    localReturnPct: number;
    ccyReturnPct: number;
    totalReturnPct: number;
  };
}

// A rebased daily index for the non-base book: the asset-only ("local") path with
// currency held flat, and the actual CHF path. Their gap is the FX contribution.
// The client rebases to a chosen window (MTD/YTD/…) client-side.
export interface FxPerformancePoint {
  date: string; // YYYY-MM-DD
  localIndex: number;
  chfIndex: number;
  benchIndex: number | null; // rebased benchmark index; null only if it can't be resolved for this date
}

export interface FxPerformanceResponse {
  asOf: string;
  baseCurrency: string;
  benchmarkLabel: string; // e.g. "S&P 500"
  series: FxPerformancePoint[]; // ascending by date; empty when there's no data
}

export interface BalanceKpis {
  netWorthChf: number;
  momPct: number | null; // net-worth month-over-month change
  /** Net worth's three parts by how reachable the money is: cash accounts
   *  net of card debt; a brokerage's holdings and cash; pensions and
   *  deposits. They add up to net worth. */
  liquidChf: number;
  investedChf: number;
  lockedChf: number;
}

/**
 * How far the transaction ledger and the statements have drifted apart for one
 * balance, or, for a brokerage's `Invested`, how far its stated figures are
 * from what its holdings were worth on their days.
 *
 * Both sides describe the same account and neither is authoritative: a
 * statement can be stale and a ledger can be incomplete, and the app cannot
 * know which. So the difference is reported and never resolved.
 *
 * `drift` is in the balance's OWN currency, and it is the divergence across the
 * whole compared span rather than a sum of absolute errors. Per-day deltas
 * mostly cancel — a payment the statement books a day later than the ledger
 * reads as a large negative followed by an equal positive — so a count of
 * disagreeing days on its own paints an account far worse than it is. Both
 * numbers are shown for that reason. Against the holdings, `drift` is the
 * newest compared figure's difference.
 */
export interface BalanceReconciliation {
  balanceId: number;
  accountId: number;
  institution: string;
  label: string;
  currency: string;
  /** What the stated figures are compared with: the account's own
   *  movements, or what its holdings were worth on each figure's day. */
  against: "activity" | "holdings";
  compared: number;
  disagreeing: number;
  drift: number | null;
  /** The last day both sides had a figure for. */
  lastComparedDate: string | null;
  /** Days the ledger worked out that no statement covers. */
  derived: number;
  /** Between which two stated figures the movements do not add up, and by how
   *  much; against the holdings, each figure they do not agree with. Both
   *  sides stand; this is what the person is asked about. */
  spans: ReconciliationSpan[];
}

/** One pair of stated figures whose movements do not carry one to the other,
 *  or one figure the holdings do not agree with, `from` and `to` its day. */
export interface ReconciliationSpan {
  from: string;
  to: string;
  stated: number;
  /** What the movements carry the earlier figure to, or what the holdings
   *  were worth. */
  implied: number;
  /** `stated − implied`, in the balance's own currency. */
  drift: number;
}

/** A ledger the app cannot reconcile, and why: an account with transactions and
 *  no balance ever recorded, or one whose balance and transactions are in
 *  different currencies. */
export interface LedgerNotice {
  accountId: number;
  institution: string;
  label: string;
  reason: "untracked" | "currency-mismatch";
  ledgerCurrency: string;
  balanceCurrency: string | null;
  transactions: number;
  /** The span the untracked ledger covers, as days. */
  firstDate: string;
  lastDate: string;
}

/** A transfer leg with no counterpart — money that appears to leave net worth
 *  and never arrive. Either a transfer to somewhere the app does not track, or
 *  an account whose statements have not been imported. */
export interface UnmatchedTransfer {
  id: number;
  accountId: number;
  institution: string;
  account: string;
  bookedDate: string;
  amount: number;
  currency: string;
  amountChf: number;
  description: string;
}

/** A sale the record has no acquisition for — almost always a holding that
 *  predates the first imported file, and fixed by entering an opening position.
 *  Surfaced on Data freshness rather than costed at zero, which would report
 *  the whole proceeds as a gain with nothing to flag it. */
export interface PositionGap {
  account: string;
  symbol: string;
  /** How much was sold with nothing behind it. */
  quantity: number;
  /** How many sales are affected. */
  sales: number;
  firstSoldDate: string;
}

/** One row of `GET /institutions`: Manage's own list, which includes an
 *  institution with no accounts. */
export interface InstitutionRow {
  id: number;
  name: string;
  /** What documents call it: the institution's identity. */
  reference: string;
  type: InstitutionType;
  accountCount: number;
  // The extensions, by name, managing this institution or any of its accounts.
  extensions: string[];
  /** What an extension manages on the institution itself, by capability. */
  managed: Partial<Record<Capability, string>>;
}

export interface BalancesResponse {
  asOf: string;
  /** Today in the display timezone — the day every "current value" is read at,
   *  and what makes the server's clock and the client's agree. */
  today: string;
  currentMonth: { year: number; month: number };
  /** The month axis every series is sampled over, oldest first, each carrying
   *  the day it is read at. */
  months: MonthSample[];
  /**
   * Every balance-sheet column as a labelled series, in canonical order
   * (`compareBalanceOrder`). This is the shape to read: each point carries its
   * own `basis`, so nothing downstream has to decide whether to carry a value
   * forward or what a blank cell meant.
   */
  series: BalanceSeries[];
  kpis: BalanceKpis;
  /** Sales with no acquisition behind them. Empty is the normal state. */
  gaps: PositionGap[];
  /** Where the ledger and the statements disagree, per balance. */
  reconciliations: BalanceReconciliation[];
  /** Ledgers no reconciliation covers, with the reason. */
  ledgerNotices: LedgerNotice[];
  /** Transfer legs whose other half never arrived. */
  unmatchedTransfers: UnmatchedTransfer[];
  /** What the person should look at: one row per open account and
   *  condition, in the order the card lists them. */
  dataQuality: DataQualityRow[];
}

/** Net worth from `from` to `today`, one value a day per slice, in the
 *  reporting currency, a liability negative. */
export interface NetWorthResponse {
  today: string;
  from: string;
  slices: NetWorthSlice[];
  values: number[][];
}

export type DataQualityCondition = (typeof DATA_QUALITY_CONDITIONS)[number];
export type DataQualityAction = (typeof DATA_QUALITY_ACTIONS)[number];

/** One condition of one account, with the one thing to do about it. The
 *  amounts and spans behind it stay in the rest of the response. */
export interface DataQualityRow {
  accountId: number;
  institution: string;
  /** The account's name: a row is about the account as a whole, never one of
   *  the balances it states. */
  account: string;
  condition: DataQualityCondition;
  /** The day the condition is measured from: the newest known figure, the
   *  day an extension's check names, for `unreconciled` the earliest figure
   *  that does not reconcile, or for `negative-cash` the first day the cash
   *  goes below zero. Null for `no-balance`. */
  since: string | null;
  action: DataQualityAction;
}

// ---- strategy ----
/** A period during which a symbol may be traded. */
export interface StockTradingWindow {
  id: number;
  symbol: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  createdAt: string;
  updatedAt: string;
}

// A comma-separated list of account ids on the wire (`?accountIds=1,2,3`) — the
// multi-select account filter shared by transactions/trades/dividends. `-1` is
// the "nothing selected" sentinel (no real account has that id) — it must
// survive here, or a deselect-all collapses to an empty array and the filter
// (`f.accountIds?.length`) is silently treated as "no filter".
const accountIdList = z
  .string()
  .transform((v) =>
    v
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isInteger(n) && n !== 0),
  )
  .optional();

// A comma-separated list of plain strings on the wire (`?primaries=Groceries,Rent`) —
// the multi-select category filter.
const stringList = z
  .string()
  .transform((v) =>
    v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
  )
  .optional();

// ---- watchlist ----
export const addWatchlistSchema = z.object({
  // In the market-data service's own notation.
  symbol: z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[A-Za-z0-9.^=-]+$/, "Invalid ticker symbol"),
  label: z.string().trim().max(40).optional(),
});

export type AddWatchlistInput = z.infer<typeof addWatchlistSchema>;

const MAX_QUOTE_SYMBOLS = 30;

/** Which provider answers for market data: `auto` (the application's own),
 *  `off`, or a registered institution's id. */
/** Whether market data answers, and which provider does. */
export interface MarketDataSource {
  available: boolean;
  institution: string | null;
}

export const quoteQuerySchema = z.object({
  symbols: z
    .string()
    .min(1)
    .refine(
      (s) =>
        s
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean).length <= MAX_QUOTE_SYMBOLS,
      `At most ${MAX_QUOTE_SYMBOLS} symbols per request`,
    ),
});

export const reorderWatchlistSchema = z.object({
  ids: z.array(z.number().int().positive()).min(1),
});

// ---- cash flow ----
// The Period comparison's rows: what the person built, or null for the rows
// the page ships. A metric names at least one part, and never the expenses
// beside one of their categories, which would count them twice. A category
// is its id, or null for the expenses nobody has filed.
const comparisonMetricSchema = z
  .object({
    name: z.string().trim().min(1).max(40).nullable(),
    in: z.array(z.enum(IN_PARTS)).max(IN_PARTS.length),
    out: z.array(z.enum(OUT_PARTS)).max(OUT_PARTS.length),
    categories: z.array(z.number().int().positive().nullable()).max(100),
  })
  .strict()
  .refine((m) => m.in.length + m.out.length + m.categories.length > 0, "Pick at least one part")
  .refine(
    (m) => !(m.out.includes("expenses") && m.categories.length > 0),
    "Expenses and their categories cannot be read together",
  );

export const comparisonLayoutSchema = z
  .object({
    rows: z
      .array(
        z
          .object({
            title: z.string().trim().max(40),
            metrics: z.array(comparisonMetricSchema).max(24),
          })
          .strict(),
      )
      .max(12),
  })
  .strict();

export const updateComparisonLayoutSchema = z
  .object({ layout: comparisonLayoutSchema.nullable() })
  .strict();

/** One metric: parts in and out, and expense categories by id, null the
 *  uncategorised; a null name takes the name of what it is. */
export type MetricSpec = z.infer<typeof comparisonMetricSchema>;
/** The comparison's rows of metrics, each row optionally titled. */
export type ComparisonLayout = z.infer<typeof comparisonLayoutSchema>;

/** What `GET /cashflow/flows` returns: every item of the whole history, each
 *  account's opening balance, and what the items point at by id. */
export interface CashflowFlowsResponse {
  today: string;
  items: FlowItem[];
  openings: Opening[];
  categories: { id: number; name: string; type: "primary" | "secondary" }[];
  accounts: { id: number; name: string; institutionId: number; institution: string }[];
}

// ---- category taxonomy management ----
// Two levels of one taxonomy: a primary category and the secondary that
// qualifies it. Only a NAME is settable — `systemKey` marks the categories the
// application itself depends on, and claiming a row for the application is a
// code change rather than user input.
const categoryName = z.string().trim().min(1).max(60);

export const createCategorySchema = z.object({ name: categoryName });

export const updateCategorySchema = z.object({ name: categoryName.optional() }).strict();

export type CreateCategoryInput = z.infer<typeof createCategorySchema>;

export type UpdateCategoryInput = z.infer<typeof updateCategorySchema>;

// ---- transactions ledger ----
const txnTag = z.string().trim().max(60);

// Filters for the ledger/review table. `needsReview=true` narrows to untagged
// rows. Pagination via limit/offset; `total` comes back for the page count.
const TXN_SORT_KEYS = ["date", "amount", "description", "account", "primary", "secondary"] as const;

export const transactionListQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
  accountIds: accountIdList,
  primaries: stringList,
  secondaries: stringList,
  needsReview: z
    .enum(["true", "false"])
    .optional()
    .transform((v) => v === "true"),
  from: dateStr.optional(),
  to: dateStr.optional(),
  sort: z.enum(TXN_SORT_KEYS).default("date"),
  dir: z.enum(["asc", "desc"]).default("desc"),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

export type TxnSortKey = (typeof TXN_SORT_KEYS)[number];

// Inline single-row edit. A category change is also fed back to the classifier
// as a correction (server-side). `null` clears a category (→ needs review).
export const updateTransactionSchema = z
  .object({
    primary: txnTag.nullable().optional(),
    secondary: txnTag.nullable().optional(),
    description: z.string().trim().min(1).max(300).optional(),
    bookedDate: dateStr.optional(),
    amount: z.coerce.number().finite().optional(),
    /** The currency the movement is in. A correction, not a conversion:
     *  nothing is recalculated, the figure is restated as what it always was. */
    currency: z.string().trim().min(1).max(8).optional(),
    /** Moving the row to another account — the fix for a statement imported
     *  against the wrong one, which is otherwise a delete and a retype. */
    accountId: z.number().int().positive().optional(),
    note: z.string().trim().max(500).nullable().optional(),
  })
  .strict();

export type UpdateTransactionInput = z.infer<typeof updateTransactionSchema>;

// Set a category on many rows at once (the review table's bulk action).
export const bulkRetagSchema = z
  .object({
    ids: z.array(z.coerce.number().int().positive()).min(1),
    primary: txnTag.nullable().optional(),
    secondary: txnTag.nullable().optional(),
  })
  .refine((v) => v.primary !== undefined || v.secondary !== undefined, {
    message: "Provide a primary or secondary to set",
  });

export type BulkRetagInput = z.infer<typeof bulkRetagSchema>;

// Apply reviewed classifier suggestions (the Auto-categorize flow). Per-row
// primary/secondary, unlike bulk-retag's single value for all rows.
export const applySuggestionsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.coerce.number().int().positive(),
        primary: txnTag,
        secondary: txnTag.nullable().optional(),
      }),
    )
    .min(1),
});

export type ApplySuggestionsInput = z.infer<typeof applySuggestionsSchema>;

// A manually-entered transaction (cash, or a card we don't auto-import).
export const createTransactionSchema = z.object({
  accountId: z.number().int().positive(),
  bookedDate: dateStr,
  amount: z.coerce.number().finite(), // signed: out negative, in positive
  currency: z.string().trim().min(1).max(8).default(BASE_CURRENCY),
  description: z.string().trim().min(1).max(300),
  primary: txnTag.nullable().optional(),
  secondary: txnTag.nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});

export type CreateTransactionInput = z.infer<typeof createTransactionSchema>;

// ---- investments: trades & dividends ----
const isoDateTime = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?$/, "Expected a date or ISO datetime");

const TRADE_SORT_KEYS = ["date", "symbol", "broker", "quantity", "price", "amount"] as const;

export const tradeListQuerySchema = z.object({
  search: z.string().trim().max(120).optional(), // symbol search
  accountIds: accountIdList,
  type: z.enum(["buy", "sell"]).optional(),
  from: dateStr.optional(),
  to: dateStr.optional(),
  sort: z.enum(TRADE_SORT_KEYS).default("date"),
  dir: z.enum(["asc", "desc"]).default("desc"),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

export type TradeSortKey = (typeof TRADE_SORT_KEYS)[number];

const DIVIDEND_SORT_KEYS = ["date", "symbol", "broker", "amount", "withholdingTax"] as const;

export const dividendListQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
  accountIds: accountIdList,
  from: dateStr.optional(),
  to: dateStr.optional(),
  sort: z.enum(DIVIDEND_SORT_KEYS).default("date"),
  dir: z.enum(["asc", "desc"]).default("desc"),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

export type DividendSortKey = (typeof DIVIDEND_SORT_KEYS)[number];

// Manual trade entry. `quantity` is a positive magnitude; direction comes from
// `type` (the service signs it). `executedAt` may carry a time.
export const createTradeSchema = z.object({
  accountId: z.number().int().positive(),
  executedAt: isoDateTime,
  symbol: z.string().trim().min(1).max(40),
  type: z.enum(["buy", "sell"]),
  quantity: z.coerce.number().finite().positive(),
  price: z.coerce.number().finite().nonnegative(),
  fee: z.coerce.number().finite().nonnegative().optional(),
  feeCurrency: z.string().trim().min(1).max(8).optional(),
  currency: z.string().trim().min(1).max(8).default(BASE_CURRENCY),
  /** This trade stands in for a holding that predates the record — the position
   *  you already had when the history starts. It cannot be inferred ("the first
   *  trade for a symbol" is equally true of every genuine first purchase), and
   *  it changes reported numbers: the money was never spent inside the reporting
   *  period, so counting it as investment expenditure reports a purchase that
   *  never happened. */
  openingPosition: z.boolean().optional(),
  /** Which lots a sell closes, by opening trade; FIFO for what it leaves. */
  lotChoice: lotChoiceSchema.optional(),
});

export type CreateTradeInput = z.infer<typeof createTradeSchema>;

export const createDividendSchema = z.object({
  accountId: z.number().int().positive(),
  paidDate: dateStr,
  symbol: z.string().trim().min(1).max(40),
  amount: z.coerce.number().finite().positive(),
  withholdingTax: z.coerce.number().finite().nonnegative().optional(),
  currency: z.string().trim().min(1).max(8).default(BASE_CURRENCY),
});

export type CreateDividendInput = z.infer<typeof createDividendSchema>;

// Inline edit of a trade or dividend row (recomputes the CHF cache server-side).
/** Editing one execution.
 *
 *  No `amount`: the cash value of a trade IS quantity x price, and a field that
 *  could be set independently is a field that could disagree with them. The
 *  realised figure is computed from the lots and is not an input. */
export const updateTradeSchema = z
  .object({
    executedAt: isoDateTime.optional(),
    symbol: z.string().trim().min(1).max(40).optional(),
    type: z.enum(["buy", "sell"]).optional(),
    quantity: z.coerce.number().finite().optional(),
    price: z.coerce.number().finite().nullable().optional(),
    fee: z.coerce.number().finite().nullable().optional(),
    feeCurrency: z.string().trim().min(1).max(8).nullable().optional(),
    /** `null` returns the sell to FIFO. */
    lotChoice: lotChoiceSchema.nullable().optional(),
  })
  .strict();

export type UpdateTradeInput = z.infer<typeof updateTradeSchema>;

/** Editing one dividend. A payment, not an execution: it has an amount and a
 *  tax withheld, and no quantity, price or market. */
export const updateDividendSchema = z
  .object({
    paidDate: dateStr.optional(),
    symbol: z.string().trim().min(1).max(40).optional(),
    amount: z.coerce.number().finite().optional(),
    withholdingTax: z.coerce.number().finite().nullable().optional(),
  })
  .strict();

export type UpdateDividendInput = z.infer<typeof updateDividendSchema>;

/**
 * What an account IS — the vocabulary that decides behaviour (lots and P/L for
 * a brokerage, one balance for a pension or a bank, a ledger for a bank).
 * The reasoning lives with the code that branches on it,
 * `src/finance/service/registry.ts`; it is declared here because the request
 * schema and the page's type picker have to agree with it, and a third copy of
 * a list like this is how two of them come to disagree.
 */

export type AccountType = (typeof ACCOUNT_TYPES)[number];

/** Correcting the account itself, as opposed to a figure it states. Every field
 *  is optional and only what is present is written — a patch that says nothing
 *  about `closedDate` must not reopen a closed account. */
export const updateAccountSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    type: z.enum(ACCOUNT_TYPES).optional(),
    /** What the institution calls it. */
    reference: z.string().trim().min(1).max(120).optional(),
    /** The currency of the account's one balance. Refused on a brokerage,
     *  and once anything has been stated or booked on the balance. */
    currency: z.string().trim().min(1).max(8).optional(),
    /** The day it stopped being an account. `null` reopens it. */
    closedDate: dateStr.nullable().optional(),
  })
  .strict();

export type UpdateAccountInput = z.infer<typeof updateAccountSchema>;

/** What kind of institution an institution is. Mostly descriptive — see
 *  `institutions.type`'s own comment in src/finance/data/schema.ts — with two
 *  exceptions: `self` names the one institution that holds what the person
 *  holds themselves, and a `broker`'s labels resolve onto its one brokerage
 *  account. */

export type InstitutionType = (typeof INSTITUTION_TYPES)[number];

export type AssetType = (typeof ASSET_TYPES)[number];

/** Correcting the institution itself, mirroring `updateAccountSchema`. Every
 *  field is optional and only what is present is written. */
export const updateInstitutionSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    type: z.enum(INSTITUTION_TYPES).optional(),
    /** What documents call it. */
    reference: z.string().trim().min(1).max(120).optional(),
  })
  .strict();

export type UpdateInstitutionInput = z.infer<typeof updateInstitutionSchema>;

/** An institution added by hand. */
export const createInstitutionSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    type: z.enum(INSTITUTION_TYPES).optional(),
    /** What documents call it; its name when omitted. */
    reference: z.string().trim().min(1).max(120).optional(),
  })
  .strict();

export type CreateInstitutionInput = z.infer<typeof createInstitutionSchema>;

/** An account added by hand, at an institution that already exists or at none. */
export const createAccountSchema = z
  .object({
    institution: z.string().trim().min(1).max(120).nullable(),
    name: z.string().trim().min(1).max(120),
    type: z.enum(ACCOUNT_TYPES),
    /** What the institution calls it; its name when omitted. */
    reference: z.string().trim().min(1).max(120).optional(),
    /** The currency of its one balance; the base currency when omitted.
     *  Ignored on a brokerage. */
    currency: z.string().trim().min(1).max(8).optional(),
  })
  .strict();

export type CreateAccountInput = z.infer<typeof createAccountSchema>;

// ---- stating a balance by hand -------------------------------------------

/** One balance, as the person enters or corrects it.
 *
 *  `amount: null` is a clear — an explicit "no value here", which is a real
 *  assertion and not an empty form. The currency is the FACET's, so it is
 *  required only when the facet is new; naming a different one for a facet
 *  that already has figures is refused rather than silently redenominating
 *  its history. */
export const createObservationSchema = z
  .object({
    asOf: dateStr,
    amount: z.number().finite().nullable(),
  })
  .strict();

export type CreateObservationInput = z.infer<typeof createObservationSchema>;

/** Correcting a stated figure. The balance is not here: moving a figure from
 *  one balance to another is deleting it and stating the other, which is two
 *  decisions and reads as two. */
export const updateObservationSchema = z
  .object({
    asOf: dateStr.optional(),
    amount: z.number().finite().nullable().optional(),
  })
  .strict();

export type UpdateObservationInput = z.infer<typeof updateObservationSchema>;

/** Whether an account holds positions the application values — lots from
 *  its trades or the holdings a statement lists, marked at the day's price:
 *  a brokerage, or a pension. */
export const valuedFromPositions = (type: string): boolean =>
  type === "brokerage" || type === "pension";

/** Whether an account defines its balances itself. Every other kind has
 *  exactly one, named after the account. */
export const definesBalances = (type: string): boolean => type === "brokerage";

/** Holds positions, so lots, FIFO and realised/unrealised P/L apply, and its
 *  activity feeds the cash-flow investment lines. */
export const isBrokerage = (type: string): boolean => type === "brokerage";

/** Whether an account's balance is what is owed on it rather than what it
 *  holds: a credit card. It subtracts from a total, and a movement out of the
 *  account adds to it. */
export const isLiability = (type: string): boolean => type === "credit_card";

/** The kinds of bank account and card, and cash: the account types whose whole
 *  balance is their transaction ledger, and whose one balance therefore
 *  follows it. Which of them an account is stays the person's on any account:
 *  a bank's export does not say. */
export const LEDGER_TYPES: ReadonlySet<string> = new Set([
  "current",
  "savings",
  "deposit",
  "credit_card",
  "prepaid_card",
  "cash",
]);

/** Defining a balance: its label and its currency, fixed from then on. */
export const createBalanceSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    currency: z.string().trim().min(1).max(8),
    groupId: z.number().int().positive().nullable().optional(),
  })
  .strict();

export type CreateBalanceInput = z.infer<typeof createBalanceSchema>;

export const updateBalanceSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    groupId: z.number().int().positive().nullable().optional(),
  })
  .strict();

export type UpdateBalanceInput = z.infer<typeof updateBalanceSchema>;

export const createGroupSchema = z.object({ name: z.string().trim().min(1).max(120) }).strict();
export type CreateGroupInput = z.infer<typeof createGroupSchema>;

export const updateGroupSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    position: z.number().int().min(0).optional(),
  })
  .strict();
export type UpdateGroupInput = z.infer<typeof updateGroupSchema>;

// ---- interest -------------------------------------------------------------

export const createInterestSchema = z
  .object({
    accountId: z.number().int().positive(),
    paidDate: dateStr,
    /** Signed: interest earned is positive, interest charged negative. */
    amount: z.number().finite(),
    currency: z.string().trim().min(1).max(8).default(BASE_CURRENCY),
    note: z.string().trim().max(400).optional(),
  })
  .strict();

export type CreateInterestInput = z.infer<typeof createInterestSchema>;

export const updateInterestSchema = z
  .object({
    paidDate: dateStr.optional(),
    amount: z.number().finite().optional(),
    note: z.string().trim().max(400).nullable().optional(),
  })
  .strict();

export type UpdateInterestInput = z.infer<typeof updateInterestSchema>;

// ---- holdings -------------------------------------------------------------

/** A position a statement reports, entered by hand. Identity is
 *  (account, asset, day), so restating a day corrects it in place. */
export const createHoldingSchema = z
  .object({
    accountId: z.number().int().positive(),
    symbol: z.string().trim().min(1).max(40),
    asOf: dateStr,
    quantity: z.number().finite(),
    statedValue: z.number().finite().nullable().optional(),
    statedCost: z.number().finite().nullable().optional(),
    /** The currency the two stated figures are in — never the held asset. */
    statedCurrency: z.string().trim().min(1).max(8).optional(),
  })
  .strict();

export type CreateHoldingInput = z.infer<typeof createHoldingSchema>;

export const updateHoldingSchema = z
  .object({
    asOf: dateStr.optional(),
    quantity: z.number().finite().optional(),
    statedValue: z.number().finite().nullable().optional(),
    statedCost: z.number().finite().nullable().optional(),
    statedCurrency: z.string().trim().min(1).max(8).nullable().optional(),
  })
  .strict();

export type UpdateHoldingInput = z.infer<typeof updateHoldingSchema>;

/**
 * Display order for institution KINDS on the balance sheet: banks, then
 * brokers, then pension wrappers, then everyone else (`self`/unset) —
 * grouped the way the page groups them. Ranks `institutions.type`
 * (bank/broker/pension/self), never an institution's own name, so a new
 * institution lands somewhere predictable the moment its extension declares what
 * kind of institution it is, instead of needing a hand-maintained name list
 * updated every time one is added or renamed.
 */
const INSTITUTION_TYPE_ORDER = ["bank", "broker", "pension", "self"] as const;

/** Leaf order within one institution: settled cash first, then other assets,
 *  then liabilities, then the reconstructed investment columns — so "Cash"
 *  always reads before "Invested" whatever order the rows arrive in. */
/** Within an institution, cash-keeping accounts read before the ones that
 *  hold positions, and a reconstructed column after the balances beside it. */
const ACCOUNT_TYPE_RANK: Record<string, number> = {
  current: 0,
  savings: 1,
  deposit: 2,
  cash: 3,
  prepaid_card: 4,
  credit_card: 5,
  brokerage: 6,
  pension: 7,
};

/** Where one institution KIND sorts — exported so a caller with live
 *  `institutions.type` data (`compareBalanceOrder`'s caller is the only one
 *  that has it; this module is pure and has no DB) can compute it once per
 *  institution before sorting. */
export function institutionTypeRank(institutionType: string | null): number {
  const i =
    institutionType == null
      ? -1
      : (INSTITUTION_TYPE_ORDER as readonly string[]).indexOf(institutionType);
  return i === -1 ? INSTITUTION_TYPE_ORDER.length : i;
}

/** Canonical balance-sheet order, shared by the API's series/export and the
 *  web's grouping so the two can never disagree. `institutionRank` is
 *  precomputed by the caller via `institutionTypeRank` — this module has no
 *  DB access, so it cannot resolve an institution's own type itself. */
export function compareBalanceOrder(
  a: {
    institution: string;
    institutionRank: number;
    accountType: string;
    reconstructed: boolean;
    name: string;
  },
  b: {
    institution: string;
    institutionRank: number;
    accountType: string;
    reconstructed: boolean;
    name: string;
  },
): number {
  return (
    a.institutionRank - b.institutionRank ||
    a.institution.localeCompare(b.institution) ||
    (ACCOUNT_TYPE_RANK[a.accountType] ?? 9) - (ACCOUNT_TYPE_RANK[b.accountType] ?? 9) ||
    Number(a.reconstructed) - Number(b.reconstructed) ||
    a.name.localeCompare(b.name)
  );
}

// null clears the DCA schedule (no reminder events get generated) — see docs/strategy.md.
export const updateDcaDayOfMonthSchema = z.object({
  day: z.number().int().min(1).max(31).nullable(),
});

// ---- strategy: trading windows ----
// A period during which a symbol may be traded — see docs/strategy.md.
const stockTradingWindowDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

export const createStockTradingWindowSchema = z
  .object({
    symbol: z.string().trim().min(1).max(40).toUpperCase(),
    startDate: stockTradingWindowDate,
    endDate: stockTradingWindowDate,
  })
  .refine((v) => v.endDate >= v.startDate, {
    message: "End date must be on or after the start date",
    path: ["endDate"],
  });

export type CreateStockTradingWindowInput = z.infer<typeof createStockTradingWindowSchema>;

export const updateStockTradingWindowSchema = z
  .object({
    symbol: z.string().trim().min(1).max(40).toUpperCase().optional(),
    startDate: stockTradingWindowDate.optional(),
    endDate: stockTradingWindowDate.optional(),
  })
  .refine((v) => v.startDate == null || v.endDate == null || v.endDate >= v.startDate, {
    message: "End date must be on or after the start date",
    path: ["endDate"],
  });

export type UpdateStockTradingWindowInput = z.infer<typeof updateStockTradingWindowSchema>;
